"use client";

import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import toast from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";
import {
  loginSchema,
  signUpSchema,
  type LoginFormData,
  type SignUpFormData,
} from "../../../lib/schemas";
import { signIn, signUp, checkUsernameAvailability } from "../../../lib/auth";
import { signIn as nextAuthSignIn } from "next-auth/react";
import { KOREAN_REGIONS } from "../../../lib/utils";
import { getPasswordStrength } from "../../../lib/utils/passwordValidation";

function LoginPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(
    null
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [signUpError, setSignUpError] = useState("");
  const [snsLoading, setSnsLoading] = useState<"google" | null>(null);
  const [autoLogin, setAutoLogin] = useState(false);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const signUpForm = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
  });

  const password = signUpForm.watch("password", "");
  const username = signUpForm.watch("username", "");

  const handleCheckUsername = async () => {
    if (!username || username.length < 4) {
      toast.error("아이디를 4자 이상 입력해주세요.");
      return;
    }

    setIsCheckingUsername(true);
    try {
      const isAvailable = await checkUsernameAvailability(username);
      setUsernameAvailable(isAvailable);
      setUsernameChecked(true);

      if (isAvailable) {
        // 불필요한 메시지 제거
        // toast.success("사용 가능한 아이디입니다!");
      } else {
        toast.error("이미 사용 중인 아이디입니다.");
      }
    } catch (error) {
      toast.error("아이디 중복확인 중 오류가 발생했습니다.");
      setUsernameAvailable(null);
      setUsernameChecked(false);
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginError("");
    try {
      console.log("🔐 로그인 시도 시작:", data.username);

      const { signInWithEmailAndPassword } = await import("firebase/auth");
      const { getFirebaseAuth } = await import(
        "../../../lib/api/firebase-ultra-safe"
      );

      const auth = getFirebaseAuth();

      // 아이디만 입력하면 로컬 이메일로 변환.
      // 이메일을 그대로 입력한 경우(@ 포함)는 그대로 사용.
      const email = data.username.includes("@")
        ? data.username.trim()
        : `${data.username.trim()}@connectone.local`;

      console.log("🔐 로그인 이메일:", email);

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        data.password
      );

      if (userCredential.user) {
        console.log("✅ 로그인 성공!");

        // 커스텀 클레임 반영을 위해 토큰 강제 갱신
        try {
          await userCredential.user.getIdToken(true);
        } catch (tokenError) {
          console.warn("토큰 갱신 실패(무시):", tokenError);
        }

        const callbackUrl = searchParams.get("callbackUrl") || "/";
        console.log("🔄 리다이렉트:", callbackUrl);
        router.push(callbackUrl);
      }
    } catch (error: any) {
      console.error("❌ 로그인 실패:", error);

      if (error.code === "auth/operation-not-allowed") {
        setLoginError("이메일/비밀번호 로그인이 활성화되지 않았습니다. 관리자에게 문의하세요.");
        toast.error("이메일/비밀번호 로그인이 활성화되지 않았습니다.");
      } else if (error.code === "auth/user-not-found") {
        setLoginError("등록되지 않은 이메일입니다.");
      } else if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential" ||
        error.code === "auth/invalid-login-credentials"
      ) {
        setLoginError("아이디 또는 비밀번호가 올바르지 않습니다.");
      } else if (error.code === "auth/invalid-email") {
        setLoginError("올바르지 않은 이메일 형식입니다.");
      } else if (error.code === "auth/too-many-requests") {
        setLoginError(
          "너무 많은 시도가 있었습니다. 잠시 후 다시 시도해주세요."
        );
      } else {
        setLoginError(
          error?.message
            ? `로그인 중 오류가 발생했습니다. (${error.code || "unknown"})`
            : "로그인 중 오류가 발생했습니다."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSignUpSubmit = async (data: SignUpFormData) => {
    if (!usernameChecked || !usernameAvailable) {
      toast.error("아이디 중복확인을 완료해주세요.");
      return;
    }
    setIsLoading(true);
    setSignUpError("");
    try {
      await signUp({
        username: data.username,
        email: data.email,
        password: data.password,
        nickname: data.nickname,
        region: data.region,
        agreeTerms: data.agreeTerms,
      });

      setIsSignUp(false);
      signUpForm.reset();
      setUsernameChecked(false);
      setUsernameAvailable(null);
    } catch (error) {
      setSignUpError(
        error instanceof Error
          ? error.message
          : "회원가입 중 오류가 발생했습니다."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setSnsLoading("google");
    try {
      const callbackUrl = searchParams.get("callbackUrl") || "/";
      console.log("🔄 Google 로그인 리다이렉트:", callbackUrl);

      await nextAuthSignIn("google", {
        callbackUrl: callbackUrl,
        redirect: true,
      });
    } catch (error) {
      console.error("구글 로그인 오류:", error);
      toast.error("구글 로그인 중 오류가 발생했습니다.");
      setSnsLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8FC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-[400px]">
        {/* 헤더 */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <img
              src="/TM_logo.png"
              alt="톤마켓"
              className="w-auto object-cover"
              style={{ height: '120px' }}
            />
          </div>
          <h1 className="text-[20px] font-semibold text-gray-900 tracking-tight">
            {isSignUp ? "회원가입" : "로그인"}
          </h1>
        </div>

        {/* 로그인 폼 */}
        {!isSignUp ? (
          <form
            className="space-y-5"
            onSubmit={loginForm.handleSubmit(onLoginSubmit)}
          >
            {/* 아이디 입력 */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                아이디
              </label>
              <input
                type="text"
                autoComplete="username"
                placeholder="아이디를 입력해주세요"
                className={`w-full h-12 px-[14px] border rounded-[12px] transition-all duration-200 focus:outline-none placeholder:text-[#B5AFCF] ${
                  loginForm.formState.errors.username || loginError
                    ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/18"
                    : "border-[#D6CFF2] focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/18"
                }`}
                {...loginForm.register("username")}
              />
              {loginForm.formState.errors.username && (
                <p className="mt-1.5 text-sm text-red-600">
                  {loginForm.formState.errors.username.message}
                </p>
              )}
            </div>

            {/* 비밀번호 입력 */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                비밀번호
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="비밀번호를 입력해주세요"
                  className={`w-full h-12 px-[14px] pr-12 border rounded-[12px] transition-all duration-200 focus:outline-none placeholder:text-[#B5AFCF] ${
                    loginForm.formState.errors.password || loginError
                      ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/18"
                      : "border-[#D6CFF2] focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/18"
                  }`}
                  {...loginForm.register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#B5AFCF] hover:text-[#7A5AF8] transition-colors"
                  aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {loginForm.formState.errors.password && (
                <p className="mt-1.5 text-sm text-red-600">
                  {loginForm.formState.errors.password.message}
                </p>
              )}
            </div>

            {/* 자동 로그인 체크박스 */}
            <div className="pt-1">
              <label
                htmlFor="autoLogin"
                className="flex items-center gap-3 cursor-pointer min-h-[42px] py-2"
              >
                <input
                  type="checkbox"
                  id="autoLogin"
                  checked={autoLogin}
                  onChange={e => setAutoLogin(e.target.checked)}
                  className="w-5 h-5 rounded border-[#D6CFF2] text-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/18 focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-sm text-gray-700">자동 로그인</span>
              </label>
            </div>

            {/* 로그인 오류 메시지 */}
            {loginError && (
              <div className="text-red-600 text-sm text-center py-2 bg-red-50 rounded-lg border border-red-100">
                {loginError}
              </div>
            )}

            {/* 로그인 버튼 */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-[52px] rounded-[14px] font-bold text-base text-white transition-all duration-200 hover:-translate-y-[1px] active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:active:scale-100"
                style={{
                  background: "linear-gradient(135deg, #7A5AF8, #A48CFF)",
                  boxShadow: "0 4px 10px rgba(122, 90, 248, 0.25)",
                }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  </div>
                ) : (
                  "로그인"
                )}
              </button>
            </div>
          </form>
        ) : (
          /* 회원가입 폼 - 간소화된 버전 (회원가입 페이지로 리다이렉트 권장) */
          <form
            className="space-y-5"
            onSubmit={signUpForm.handleSubmit(onSignUpSubmit)}
          >
            <div className="text-center text-sm text-gray-600 mb-4">
              더 편리한 회원가입을 위해{" "}
              <Link
                href="/auth/signup"
                className="text-[#7A5AF8] font-semibold hover:underline"
              >
                회원가입 페이지
              </Link>
              로 이동하시겠어요?
            </div>
          </form>
        )}

        {/* Divider */}
        <div className="relative my-7">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-[#DDD7F2]" />
          </div>
          <div className="relative flex justify-center">
            <span className="px-3 bg-[#F9F8FC] text-[12px] text-[#A49BCB]">
              또는
            </span>
          </div>
        </div>

        {/* 구글 로그인 버튼 */}
        <div className="mb-8">
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={snsLoading === "google"}
            className="w-full h-12 bg-white border border-[#E0DDF5] rounded-[12px] text-gray-700 font-medium hover:bg-[#F2EEFF] active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            {snsLoading === "google" ? (
              <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>구글로 로그인</span>
              </>
            )}
          </button>
        </div>

        {/* 하단 링크들 */}
        <div className="space-y-4">
          {/* 로그인/회원가입 링크 */}
          <div className="text-center">
            {isSignUp ? (
              <button
                onClick={() => {
                  setIsSignUp(false);
                  loginForm.reset();
                }}
                className="text-[#7A5AF8] font-semibold hover:underline text-sm"
              >
                로그인하기
              </button>
            ) : (
              <Link
                href="/auth/signup"
                className="text-[#7A5AF8] font-semibold hover:underline text-sm"
              >
                회원가입하기
              </Link>
            )}
          </div>

          {/* 기타 링크들 */}
          <div className="flex justify-center items-center gap-3 text-sm">
            <Link
              href="/auth/find-email"
              className="text-[#7A5AF8] font-semibold hover:underline"
            >
              아이디 찾기
            </Link>
            <span className="text-[#DDD7F2]">|</span>
            <Link
              href="/auth/reset-password"
              className="text-[#7A5AF8] font-semibold hover:underline"
            >
              비밀번호 찾기
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LoginPageContent />
    </Suspense>
  );
}
